import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { ReportingParametersParams } from '../../scheduler/store/types';
import { Props } from '../container';
import { LoadingIndicator, SystemMinimumDate, WO2Select, WO2ToggleSwitch } from 'src/common';
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import WO2Button from 'src/common/components/button/Button';
import { MergeReportsToggle } from 'src/features/clients/client/reportsV2/components/MergeReportsToggle';
import { ReportFormatOption } from 'src/features/clients/client/reportsV2/components/ReportFormatOption';
import { UploadReportAttachment } from 'src/features/clients/client/reportsV2/components/UploadReportAttachment';
import { ReportAttachmentType, ReportFormat, SaveReportingParametersRequest } from 'src/features/clients/client/reportsV2/store/types';
import { DateRange } from 'src/store';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { GetFilteredClientEntity, ReportParametersKind } from '../../store/types';
import { ReportingComponentList } from 'src/features/clients/client/reportsV2/components/ReportingComponentList';
import { ReportingComponentListOptions } from 'src/features/clients/client/reportsV2/store/constants';
import { createInitiateReportRequest, getCanMergeReportsState, getReportsToSort } from 'src/features/clients/client/reportsV2/store/helper';
import { LoadingButton } from '@mui/lab';
import ItemOrdering from 'src/common/components/ItemOrdering';

export const ReportingParameters = (props: Props): JSX.Element => {
  const { id } = useParams<ReportingParametersParams>();
  const [fullReports, setFullReports] = useState<boolean>(false);
  const [currentReportType, setCurrentReportType] = useState<ReportAttachmentType>(ReportAttachmentType.None);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [name, setName] = useState<string>('');
  const [kind, setKind] = useState<ReportParametersKind>('C');
  const [visibleToAfsl, setVisibleToAfsl] = useState(false);
  const [sharingAfsl, setSharingAfsl] = useState<GetFilteredClientEntity | null>(null);

  const parameters = props.parameters;

  const canMergeReportsState = getCanMergeReportsState(parameters);

  const history = useHistory();
  const location = useLocation();
  const routeMatch = useRouteMatch();
  const isEditing = id !== undefined;

  useEffect(() => {
    if (id && location.pathname.includes('parameters/edit')) {
      props.getReportTemplate(id);
    } else {
      props.resetReports();
    }
  }, [id]);

  useEffect(() => {
    props.setInceptionDate(SystemMinimumDate);
    props.fetchAfsls();
    setName('');
    setVisibleToAfsl(false);
    setKind('C');
  }, []);

  useEffect(() => {
    if (isEditing) {
      const parameters = props.parameters.reportingParameter;
      setVisibleToAfsl(parameters.visibleToAfsl ?? false);
      setName(parameters.parameterName ?? '');
      setKind(parameters.kind ? (parameters.kind as ReportParametersKind) : 'C');

      if (parameters.afslId) {
        setSharingAfsl({
          entityCoreId: parameters.afslId,
          name: parameters.afslName ?? '',
        });
      }
    }
  }, [props.parameters.reportingParameter]);

  const handleClickIncludeCoverLetter = () => {
    props.setIncludeCoverLetterParameter(!parameters.includeCoverLetter);
    setIsMenuOpen(!parameters.includeCoverLetter);
    setCurrentReportType(ReportAttachmentType.CoverLetter);
  };

  useEffect(() => {
    if (!props.hasClientEditAdminOnlyPermission && visibleToAfsl && props.afsls.length === 1 && !sharingAfsl) {
      setSharingAfsl(props.afsls[0]);
    }
  }, [props.afsls, visibleToAfsl]);

  const handleChangeToggleSwitch = () => {
    const toggling = !visibleToAfsl;
    setVisibleToAfsl(toggling);
    if (!props.hasClientEditAdminOnlyPermission) {
      if (toggling && props.afsls.length === 1) {
        setSharingAfsl(props.afsls[0]);
      } else if (!toggling) {
        setSharingAfsl(null);
      }
    }
  };

  const closeUploader = () => {
    setIsMenuOpen(false);

    currentReportType === ReportAttachmentType.CoverLetter ? props.setIncludeCoverLetterParameter(false) : props.setIncludeAppendixParameter(false);
  };

  const setFile = (file: File) => {
    setIsMenuOpen(false);

    if (currentReportType === ReportAttachmentType.CoverLetter) {
      props.saveCoverPageFile(file);
    } else if (currentReportType === ReportAttachmentType.Appendix) {
      props.saveAppendixFile(file);
    }
  };

  const handleClickIncludeLogo = () => {
    props.setIncludeLogoParameter(!parameters.includeLogo);
  };

  const updateReportDates = (dates: DateRange) => {
    props.setDateParameter(dates);
  };

  const handleClickIncludeAppendix = () => {
    props.setIncludeAppendixParameter(!parameters.includeAppendix);
    setIsMenuOpen(!parameters.includeAppendix);
    setCurrentReportType(ReportAttachmentType.Appendix);
  };

  return (
    <LoadingIndicator
      progress={{
        hasErrors: isEditing && props.getReportTemplateProgress.hasErrors,
        isLoading: isEditing && props.getReportTemplateProgress.isLoading,
      }}
    >
      <Container>
        <Grid container direction="column" spacing={2}>
          <Grid item mb="10px">
            <Breadcrumbs aria-label="breadcrumb">
              <Typography
                sx={{
                  ':hover': {
                    cursor: 'pointer',
                  },
                }}
                color="inherit"
                onClick={() => history.push('/reporting?tab=report-parameters')}
              >
                Report Parameters
              </Typography>
              <Typography color="textPrimary">{isEditing ? parameters.parameterName : 'Create'}</Typography>
            </Breadcrumbs>
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={'Reporting Parameter Name'}
                variant="outlined"
                fullWidth
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="parameters-type">Parameters kind</InputLabel>
                <WO2Select
                  labelId="parameters-kind"
                  label="parameters kind"
                  onChange={(e) => setKind(e.target.value as ReportParametersKind)}
                  value={kind}
                >
                  <MenuItem value="C">Client</MenuItem>
                  <MenuItem value="B">Bulk</MenuItem>
                  <MenuItem value="S">Scheduled</MenuItem>
                </WO2Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid item>
            <Box alignItems="center" display="flex" gap={2}>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <WO2ToggleSwitch
                  checked={visibleToAfsl}
                  onChange={handleChangeToggleSwitch}
                  color="primary"
                  name="visibleToAfsl"
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                />
                <Typography noWrap variant="h4">
                  Share with AFSL
                </Typography>
              </Box>
              {visibleToAfsl && (
                <FormControl fullWidth>
                  <Autocomplete
                    noOptionsText="No items to select"
                    id="afsl-multiselect"
                    fullWidth
                    options={props.afsls}
                    loading={false}
                    value={sharingAfsl}
                    getOptionLabel={(option: GetFilteredClientEntity) => option.name}
                    renderInput={(params) => <TextField {...params} margin="normal" label="AFSL" fullWidth />}
                    onChange={(e: React.SyntheticEvent<Element, Event>, afsl: GetFilteredClientEntity | null) => {
                      setSharingAfsl(afsl);
                    }}
                    isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) =>
                      option.entityCoreId === value.entityCoreId
                    }
                  />
                </FormControl>
              )}
            </Box>
          </Grid>

          <Grid item>
            <Divider />
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs={3}>
              <Card elevation={0}>
                <CardContent>
                  <Typography mb={2} variant="h3">
                    Report Parameters
                  </Typography>
                  <DateRangePicker dateRange={parameters?.dates} setDateRange={updateReportDates} inceptionDate={parameters.inceptionDate} />
                  <Box mt="15px">
                    <MergeReportsToggle
                      mergeReports={parameters.mergeReports}
                      setMergeReportsParameter={props.setMergeReportsParameter}
                      setShowItemOderingModalParameter={props.setShowItemOderingModalParameter}
                      disabled={!canMergeReportsState.canMergeReports || parameters.reportFormat === ReportFormat.JSON}
                      canChangeReportOrder={canMergeReportsState.canChangeReportOrder}
                    />
                    <Grid container style={{ paddingTop: '10px' }}>
                      <WO2ToggleSwitch
                        checked={parameters.includeLogo}
                        onChange={handleClickIncludeLogo}
                        color="primary"
                        name="includeLogo"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                      />
                      <Typography variant="h4" style={{ paddingTop: '10px' }}>
                        Include Logo
                      </Typography>
                    </Grid>
                    <Grid container style={{ paddingTop: '10px' }}>
                      <WO2ToggleSwitch
                        checked={parameters.includeCoverLetter}
                        onChange={handleClickIncludeCoverLetter}
                        color="primary"
                        name="includeCoverPage"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
                      />
                      <Typography variant="h4" style={{ paddingTop: '10px' }}>
                        Cover Page
                      </Typography>
                    </Grid>
                    <Grid container style={{ paddingTop: '10px' }}>
                      <WO2ToggleSwitch
                        checked={parameters.includeAppendix}
                        onChange={handleClickIncludeAppendix}
                        color="primary"
                        name="includeAppendix"
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        disabled={parameters.reportFormat == ReportFormat.Excel || parameters.reportFormat == ReportFormat.JSON}
                      />
                      <Typography variant="h4" style={{ paddingTop: '10px' }}>
                        Appendix
                      </Typography>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item container display="flex" direction="column" xs={9} mt={2} spacing={1}>
              <Grid item container spacing={2} alignItems="center">
                <Grid item flex={1}>
                  <Box gap={2} display="flex" alignItems="center">
                    <WO2Button variant="contained" onClick={props.expandAll}>
                      Expand
                    </WO2Button>
                    <ReportFormatOption
                      format={parameters?.reportFormat}
                      setReportFormat={props.setReportFormat}
                      hasClientEditAdminOnlyPermission={props.hasClientEditAdminOnlyPermission}
                      isDisplayOnly={false}
                    />
                    {props.hasClientEditAdminOnlyPermission && (
                      <Box display="flex" alignItems="center">
                        <WO2ToggleSwitch checked={fullReports} onChange={(e) => setFullReports(e.target.checked)} color="primary" />
                        <Typography variant="h4">Full Reports</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item>
                  <LoadingButton
                    loading={props.createReportingParameterProgress.isLoading || props.updateReportingParameterProgress.isLoading}
                    disabled={name.length === 0 || (visibleToAfsl && !props.hasClientEditAdminOnlyPermission && !sharingAfsl)}
                    onClick={() => {
                      const request = createInitiateReportRequest({
                        isDataFromTriumph: props.isDataFromTriumph,
                        parameters: parameters,
                        handleClickMergeReports: () => props.setMergeReportsParameter(!parameters.mergeReports),
                      });

                      const payload: SaveReportingParametersRequest = {
                        ...request.reportRequest,
                        visibleToAfsl: visibleToAfsl,
                        parameterName: name,
                      };

                      if (!isEditing) {
                        props
                          .createReportingParameter({
                            reportParameters: payload,
                            afslId: sharingAfsl?.entityCoreId ?? undefined,
                            kind: kind,
                          })
                          .then(() => {
                            history.push('/reporting?tab=report-parameters');
                          });
                      } else {
                        props
                          .updateReportingParameter({
                            reportParameters: payload,
                            reportParameterId: id,
                            afslId: sharingAfsl?.entityCoreId ?? undefined,
                            kind: kind,
                          })
                          .then(() => {
                            history.push('/reporting?tab=report-parameters');
                          });
                      }
                    }}
                    variant="contained"
                  >
                    {isEditing ? 'Save' : 'Create'}
                  </LoadingButton>
                </Grid>
              </Grid>

              <ReportingComponentList
                history={history}
                location={location}
                match={routeMatch}
                {...props}
                ignoreClientSubCheck
                disablePortfolioSelect
                reportingList={ReportingComponentListOptions}
                fullReports={fullReports}
                allowConsolidation
              />
            </Grid>
          </Grid>
        </Grid>

        <UploadReportAttachment isOpen={isMenuOpen} handleImport={setFile} handleClose={closeUploader} title={`Upload ${currentReportType}`} />
        <ItemOrdering
          itemsToSort={getReportsToSort(parameters.reportsToSort, parameters.reportFormat)}
          setSelectedReport={props.setSelectedReportOrderParameter}
          setShowItemOrderingModal={props.setShowItemOderingModalParameter}
          generateReport={() => props.setShowItemOderingModalParameter(false)}
          isOpen={parameters.showItemOrdering}
        />
      </Container>
    </LoadingIndicator>
  );
};
